import { useEffect, useRef } from "react";
import type { GeoJSONSource, Map as MlMap } from "maplibre-gl";
import { useSession } from "../app/session";

const DEFAULT_TILES = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";

export interface AreaShape {
  id: number;
  lat: number;
  lon: number;
  reachKm: number;
  enabled: boolean;
}

/** A circle as a GeoJSON ring (64 points), good enough at the scale of a search area. */
function ring(lat: number, lon: number, km: number): [number, number][] {
  const out: [number, number][] = [];
  for (let i = 0; i <= 64; i++) {
    const a = (i / 64) * 2 * Math.PI;
    const dLat = (km / 111.32) * Math.cos(a);
    const dLon = (km / (111.32 * Math.cos((lat * Math.PI) / 180))) * Math.sin(a);
    out.push([lon + dLon, lat + dLat]);
  }
  return out;
}

function shapes(areas: AreaShape[]) {
  return {
    type: "FeatureCollection" as const,
    features: areas.map((a) => ({
      type: "Feature" as const,
      properties: { id: a.id, on: a.enabled ? 1 : 0 },
      geometry: { type: "Polygon" as const, coordinates: [ring(a.lat, a.lon, Math.max(0.05, a.reachKm))] },
    })),
  };
}

/**
 * The search areas on a map (ADR 0025), each as far as it reaches; a click adds an area there. MapLibre is loaded
 * when the map is shown, as the map widget does; without WebGL the list alone does the job.
 */
export function AreaMap({
  areas,
  onAdd,
  reducedMotion,
}: {
  areas: AreaShape[];
  onAdd?: (lat: number, lon: number) => void;
  reducedMotion: boolean;
}) {
  const el = useRef<HTMLDivElement>(null);
  const map = useRef<MlMap | null>(null);
  const latest = useRef({ areas, onAdd });
  const tileUrl = useSession((s) => s.session?.features?.tileUrl) || DEFAULT_TILES;
  useEffect(() => {
    latest.current = { areas, onAdd };
  });
  useEffect(() => {
    let gone = false;
    void import("maplibre-gl").then(({ default: maplibregl }) => {
      if (gone || !el.current) return;
      const first = latest.current.areas[0];
      const m = new maplibregl.Map({
        container: el.current,
        style: {
          version: 8,
          sources: {
            tiles: {
              type: "raster",
              tiles: [tileUrl],
              tileSize: 256,
              attribution: "© OpenStreetMap",
              maxzoom: 19,
            },
          },
          layers: [{ id: "tiles", type: "raster", source: "tiles" }],
        },
        center: first ? [first.lon, first.lat] : [10, 51],
        zoom: first ? 9 : 4,
        attributionControl: { compact: true },
        fadeDuration: reducedMotion ? 0 : 300,
      });
      m.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
      const accent =
        getComputedStyle(document.documentElement).getPropertyValue("--accent").trim() || "#3b6fd8";
      m.on("load", () => {
        m.addSource("areas", { type: "geojson", data: shapes(latest.current.areas) });
        m.addLayer({
          id: "areas-fill",
          type: "fill",
          source: "areas",
          paint: { "fill-color": accent, "fill-opacity": ["case", ["==", ["get", "on"], 1], 0.15, 0.04] },
        });
        m.addLayer({
          id: "areas-line",
          type: "line",
          source: "areas",
          paint: { "line-color": accent, "line-width": 1.5 },
        });
      });
      m.on("click", (e) => latest.current.onAdd?.(e.lngLat.lat, e.lngLat.lng));
      map.current = m;
    });
    return () => {
      gone = true;
      map.current?.remove();
      map.current = null;
    };
  }, [tileUrl, reducedMotion]);
  useEffect(() => {
    const src = map.current?.getSource("areas") as GeoJSONSource | undefined;
    src?.setData(shapes(areas));
  }, [areas]);
  return <div ref={el} className="h-64 w-full overflow-hidden rounded-md" data-testid="area-map" />;
}
